import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  Box,
  TextField,
  Button,
  useMediaQuery,
  useTheme,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Typography,
  Chip,
  Grid,
  CircularProgress,
  ListItemText,
  Modal,
} from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel"; // For the X mark
import {
  buttonHoverColor,
  fontfamily,
  primaryColor,
} from "../../Util/UtilColorPalate";
import { onboardingCreateApi, OtpVerifyApi } from "../../Util/UtilAPi";
import UtilToastMessages from "../../Util/UtilToastMessages";
import { styled } from "@mui/material/styles";
import { currencies, countryCodes, ORG } from "../Data";

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    fontFamily: `${fontfamily} !important`,
    borderRadius: 8,
    "&.Mui-focused fieldset": {
      borderColor: primaryColor,
      fontFamily: `${fontfamily} !important`,
    },
  },
  "& .MuiInputLabel-root": {
    fontFamily: `${fontfamily} !important`,
    "&.Mui-focused": {
      color: primaryColor,
      fontFamily: `${fontfamily} !important`,
    },
    "& .MuiInputLabel-asterisk": {
      color: "red",
    },
  },
  "& .MuiOutlinedInput-input::placeholder": {
    fontFamily: `${fontfamily} !important`,
  },
  "& .MuiFormHelperText-root": {
    fontFamily: `${fontfamily} !important`,
  },
}));

const StyledCheckbox = styled(Checkbox)(({ theme }) => ({
  "&.Mui-checked": {
    color: primaryColor,
  },
}));

const StyledOtpInput = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    fontFamily: `${fontfamily} !important`,
    borderRadius: 8,
    width: 48,
    height: 48,
    "& input": {
      textAlign: "center",
      fontSize: "1.5rem",
      padding: 0,
    },
    "&.Mui-focused fieldset": {
      borderColor: primaryColor,
    },
  },
}));

const countryToCurrencyMap = {
  "+1": "USD", // USA
  "+44": "GBP", // UK
  "+91": "INR", // India
  "+81": "JPY", // Japan
  "+86": "CNY", // China
  "+61": "AUD", // Australia
  "+27": "ZAR", // South Africa
  "+33": "EUR", // France (and other EU countries)
};

const UserDetails = ({ onNext, companyDetails, checkoutDetails,fetchDetails }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const mandatoryCurrencies = [
    { value: "ZAR", label: "R South African Rand", symbol: "R" },
    { value: "USD", label: "$ Dollar", symbol: "$" },
    { value: "INR", label: "₹ Indian Rupee", symbol: "₹" },
  ];

  const [filteredCurrencies, setFilteredCurrencies] = useState(currencies);
  const [isLoadingRegion, setIsLoadingRegion] = useState(false);
  const [isVerify, setIsVerify] = useState(false);
  const [formData, setFormData] = useState({
    title: "Mr.",
    firstName: "",
    lastName: "",
    contactNumber: "",
    mobile: "",
    countryCode: "",
    countryCode2: "",
    companyName: "",
    emailAddress: "",
    currency: "",
    method: [],
    consentProvided: false,
  });
  const [openCancelModal, setOpenCancelModal] = useState(false);
  const [openOtpModal, setOpenOtpModal] = useState(false);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]); // Array for 6 digits
  const [otpError, setOtpError] = useState("");
  const otpInputs = useRef([]); // Refs for OTP input fields

  const handleCancel = () => {
    setOpenCancelModal(true);
  };

  const handleCloseModal = () => {
    setOpenCancelModal(false);
    detectRegionAndSetCurrencies();
    setFormData({
      title: "Mr.",
      firstName: "",
      lastName: "",
      contactNumber: "",
      mobile: "",
      countryCode: "",
      countryCode2: "",
      companyName: "",
      emailAddress: "",
      currency: "",
      method: [],
      consentProvided: true,
    });
  };
  const handleCloseOtpModal = () => {
    setOpenOtpModal(false);
    setOtp(["", "", "", "", "", ""]);
    setOtpError("");
  };

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "",
  });

  const communicationMethods = ["Email", "Whatsapp", "Call", "SMS"];
  const titles = ["Mr.", "Mrs.", "Miss.", "Dr."];
  const regionCurrencyMap = {
    US: { primary: "USD", secondary: ["CAD", "MXN"] },
    GB: { primary: "GBP", secondary: ["EUR"] },
    EU: { primary: "EUR", secondary: ["GBP", "CHF"] },
    ZA: { primary: "ZAR", secondary: ["USD"] },
    AU: { primary: "AUD", secondary: ["NZD", "USD"] },
    CA: { primary: "CAD", secondary: ["USD"] },
    IN: { primary: "INR", secondary: ["USD"] },
    JP: { primary: "JPY", secondary: ["USD"] },
    CN: { primary: "CNY", secondary: ["USD"] },
  };

  const validateField = useCallback((name, value) => {
  switch (name) {
  case "firstName":
  case "lastName":
    if (!value)
      return `${name === "firstName" ? "First" : "Last"} name is required`;

    if (!/^[A-Za-z\s]+$/.test(value))
      return `${name === "firstName" ? "First" : "Last"} name should contain only letters`;

    return "";

  case "emailAddress":
    if (!value) return "Email is required";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value))
      return "Invalid email address";
    return "";

  case "contactNumber":
    if (!value) return "Contact number is required";
    if (!/^\d+$/.test(value))
      return "Contact number must contain only digits";
    return "";

  case "mobile":
    if (value && !/^\d+$/.test(value))
      return "Alternate Contact Number must contain only digits";
    return "";

  case "title":
  case "companyName":
  case "countryCode":
    return value
      ? ""
      : `${name.charAt(0).toUpperCase() + name.slice(1)} is required`;

  case "currency":
    if (!value || value.length === 0)
      return "Currency is required";
    break;

  case "method":
    if (!value || value.length === 0)
      return "Preferred communication method is required";
    break;

  default:
    return "";
}
  }, []);

  const detectRegionAndSetCurrencies = async () => {
    setIsLoadingRegion(true);
    try {
      const res = await fetch("https://ipapi.co/json/");
      const data = await res.json();
      const callingCode = data.country_calling_code || "+1";
      let apiCurrency = data.currency || "USD";
      // Adjust for special USD value
      const getCurrencyValue = (cur) => (cur === "USD" ? "USD" : cur);
      const defaultCurrency = getCurrencyValue(apiCurrency);
      setFormData((prev) => ({
        ...prev,
        countryCode: callingCode,
        countryCode2: callingCode,
        currency: defaultCurrency,
      }));

      // Determine region for filtering
      let region = data.country_code;
      if (data.in_eu) {
        region = "EU";
      }
      const regionCurr = regionCurrencyMap[region];
      let availableCurrencies = mandatoryCurrencies;
      if (regionCurr) {
        const regionCurrencies = [regionCurr.primary, ...regionCurr.secondary]
          .map(getCurrencyValue)
          .map((p) => currencies.find((c) => c.value === p))
          .filter(Boolean);
        // Combine mandatory currencies with region-specific currencies, avoiding duplicates
        const combinedCurrencies = [...mandatoryCurrencies];
        regionCurrencies.forEach((rc) => {
          if (!combinedCurrencies.find((mc) => mc.value === rc.value)) {
            combinedCurrencies.push(rc);
          }
        });
        availableCurrencies = combinedCurrencies;
      }
      setFilteredCurrencies(availableCurrencies);
    } catch (err) {
      console.error("Error fetching location data:", err);
      // Fallback to defaults
      setFormData((prev) => ({
        ...prev,
        countryCode: "+1",
        countryCode2: "+1",
        currency: "USD",
      }));
      setFilteredCurrencies(mandatoryCurrencies);
    } finally {
      setIsLoadingRegion(false);
    }
  };

  useEffect(() => {
    detectRegionAndSetCurrencies();
  }, []);

  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === "checkbox" ? checked : value;

    if (name === "countryCode") {
      const getCurrencyValue = (cur) => (cur === "USD" ? "USD" : cur);
      let newCurrency = getCurrencyValue(countryToCurrencyMap[value] || "USD");
      setFormData((prev) => ({
        ...prev,
        [name]: newValue,
        currency: newCurrency,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: newValue,
      }));
    }

    setErrors((prev) => ({
      ...prev,
      [name]: validateField(name, newValue),
    }));
  }, [validateField]);

  // Handle OTP input changes
  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return; // Allow only digits

    const newOtp = [...otp];
    newOtp[index] = value.slice(-1); // Take only the last digit
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      otpInputs.current[index + 1].focus();
    }

    // Validate OTP when all digits are entered
    const otpString = newOtp.join("");
    if (otpString.length === 6) {
      setOtpError("");
    } else {
      setOtpError("");
    }
  };

  // Handle key down events for OTP inputs
  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpInputs.current[index - 1].focus();
    } else if (e.key === "Enter" && otp.join("").length === 6) {
      handleOtpSubmit(); // Trigger OTP submission on Enter if all digits are filled
    }
  };

  // Handle OTP paste
  const handleOtpPaste = (e) => {
    const pastedData = e.clipboardData.getData("text").trim();
    if (/^\d{6}$/.test(pastedData)) {
      const newOtp = pastedData.split("");
      setOtp(newOtp);
      otpInputs.current[5].focus();
    }
    e.preventDefault();
  };

  const handleOtpSubmit = async () => {
    const otpString = otp.join("");
    const leadid = localStorage.getItem("leadid");
    const selectedCurrency = filteredCurrencies.find((c) => c.value === formData.currency);
    const formattedCurrency = selectedCurrency
      ? `${selectedCurrency.value} - ${selectedCurrency.symbol}`
      : `${formData.currency} - $`;
    const payload = {
      action: "lead",
      data: {
        isconverted: false,
        title: formData.title,
        preferredcommunicationmethod: formData?.method.join(";"),
        consentprovided: formData.consentProvided,
        firstname: formData.firstName,
        lastname: formData.lastName,
        companyname: formData.companyName,
        currency: formattedCurrency,
        origin: "onboarding",
        email: formData.emailAddress,
        mobile: formData.mobile === "" ? "" : formData.countryCode2?.replace("+", "") + formData.mobile,
        phone: formData.countryCode?.replace("+", "") + formData.contactNumber,
      },
    };
    const data = {
      otp: otpString,
      lead: leadid,
    };
    setIsVerify(true);
    try {
      const response = await OtpVerifyApi(data);
      if (response?.statusCode === 200 || response?.statusCode === 201) {
        setSnackbar({
          open: true,
          message: response?.message,
          severity: "success",
        });
        const currency = formattedCurrency;
        setTimeout(() => {
          onNext({ payload, currency });
        }, 2000);
      }
    } catch (err) {
      setIsVerify(false);
      setSnackbar({
        open: true,
        message: err?.response?.data?.message,
        severity: "",
      });
    } finally {
      setIsVerify(false);
    }
  };

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault(); // Prevent default form submission
    const newErrors = Object.keys(formData).reduce((acc, key) => {
      const error = validateField(key, formData[key]);
      if (error) acc[key] = error;
      return acc;
    }, {});


    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    if (formData.consentProvided === false) {
      setSnackbar({
        open: true,
        message: "You must agree to be contacted by the company",
        severity: "",
      });
      return;
    }

    const selectedCurrency = filteredCurrencies.find((c) => c.value === formData.currency);
    const formattedCurrency = selectedCurrency
      ? `${selectedCurrency.value} - ${selectedCurrency.symbol}`
      : `${formData.currency} - $`;
    const currency = formattedCurrency;
    const payload = {
      action: "lead",
      data: {
        isconverted: false,
        title: formData.title,
        preferredcommunicationmethod: formData?.method.join(";"),
        consentprovided: formData.consentProvided,
        firstname: formData.firstName,
        lastname: formData.lastName,
        companyname: formData.companyName,
        currency: formattedCurrency,
        origin: "onboarding",
        classification: ORG,
        email: formData.emailAddress,
        mobile: formData.mobile === "" ? null : formData.countryCode2.replace("+", "") + formData.mobile,
        phone: formData.countryCode.replace("+", "") + formData.contactNumber,
      },
    };
    setIsSubmitting(true);
    //  fetchDetails();
    // console.log(checkoutDetails.stage,"stage new process")

    try {
      const response = await onboardingCreateApi(payload);
     
      if (response?.statusCode === 200 || response?.statusCode === 201) {
        localStorage.setItem("leadid", response.data.recordid);
         
        setOpenOtpModal(true);
      } else {
        // throw new Error(response.message);
      }
    } catch (err) {
      if (err?.response?.data?.message.includes("Your Organization is already registered")) {

        setSnackbar({
          open: true,
          message: err?.response?.data?.message || "Please allow location to proceed further",
          severity: "",
        });
        if(err?.response?.data?.data?.domain){

          setTimeout(() => {
           window.open(err?.response?.data?.data?.domain+"/Contracts", "_blank");
         }, 2000);
        }
      }
      else {
  setSnackbar({
          open: true,
          message: err?.response?.data?.message || "Please allow location to proceed further",
          severity: "",
        });
    
      }
      // else {
      //   setTimeout(() => {
      //     window.open("https://crm.bigfivecrm.com/", "_blank");
      //   }, 2000);
      // }
      
        // setTimeout(() => {
        //   onNext({ payload, currency });
        // }, 2000);
      setIsSubmitting(false);

    } finally {
      setIsSubmitting(false);
    }
  }, [formData, onNext]);

  // Prevent form submission on Enter key
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault(); // Prevent default Enter behavior
    }
  };

  const handleSnackbarClose = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", p: 2 }}>
      <form onSubmit={handleSubmit} onKeyDown={handleKeyDown}>
        <Grid container spacing={2}>
          <Grid item xs={12} container spacing={2}>
            <Grid item xs={12} sm={1.2} md={1.2}>
              <StyledTextField
                select
                label="Title"
                required
                name="title"
                value={formData.title}
                onChange={handleChange}
                error={!!errors.title}
                helperText={errors.title}
                size="small"
                fullWidth
              >
                {titles.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </StyledTextField>
            </Grid>
            <Grid item xs={12} sm={4.8}>
              <StyledTextField
                label="First Name"
                name="firstName"
                required
                value={formData.firstName}
                onChange={handleChange}
                error={!!errors.firstName}
                helperText={errors.firstName}
                size="small"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <StyledTextField
                label="Last Name"
                name="lastName"
                required
                value={formData.lastName}
                onChange={handleChange}
                error={!!errors.lastName}
                helperText={errors.lastName}
                size="small"
                fullWidth
              />
            </Grid>
          </Grid>
          <Grid item xs={12} container spacing={2}>
            <Grid item xs={12} sm={6}>
              <StyledTextField
                label="Company Name"
                name="companyName"
                required
                value={formData.companyName}
                onChange={handleChange}
                error={!!errors.companyName}
                helperText={errors.companyName}
                size="small"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <StyledTextField
                select
                label="Currency"
                name="currency"
                value={formData.currency}
                onChange={handleChange}
                error={!!errors.currency}
                helperText={errors.currency}
                size="small"
                fullWidth
                required
                disabled={isLoadingRegion}
                SelectProps={{
                  renderValue: (value) => (
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      {filteredCurrencies.find((c) => c.value === value)?.label}
                    </Box>
                  ),
                }}
              >
                {filteredCurrencies.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </StyledTextField>
            </Grid>
          </Grid>
          <Grid item xs={12} container spacing={2}>
            <Grid item xs={12} sm={2}>
              <StyledTextField
                select
                label="Country Code"
                name="countryCode"
                value={formData.countryCode || ""}
                onChange={handleChange}
                error={!!errors.countryCode}
                helperText={errors.countryCode || (isLoadingRegion ? "Detecting..." : "")}
                size="small"
                fullWidth
                required
                disabled={isLoadingRegion}
              >
                {countryCodes.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </StyledTextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <StyledTextField
                label="Contact Number"
                name="contactNumber"
                required
                type="tel"
                value={formData.contactNumber}
                onChange={handleChange}
                error={!!errors.contactNumber}
                helperText={errors.contactNumber}
                size="small"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <StyledTextField
                select
                label="Country Code"
                name="countryCode2"
                value={formData.countryCode2 || ""}
                onChange={handleChange}
                error={!!errors.countryCode2}
                helperText={errors.countryCode2 || (isLoadingRegion ? "Detecting..." : "")}
                size="small"
                fullWidth
                disabled={isLoadingRegion}
              >
                {countryCodes.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </StyledTextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <StyledTextField
                label="Alternate Contact Number"
                name="mobile"
                type="tel"
                value={formData.mobile}
                onChange={handleChange}
                error={!!errors.mobile}
                helperText={errors.mobile}
                size="small"
                fullWidth
              />
            </Grid>
          </Grid>
          <Grid item xs={12} container spacing={2}>
            <Grid item xs={12} sm={6}>
              <StyledTextField
                label="Email Address"
                name="emailAddress"
                type="email"
                required
                value={formData.emailAddress}
                onChange={handleChange}
                error={!!errors.emailAddress}
                helperText={errors.emailAddress}
                size="small"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <StyledTextField
                select
                label="Preferred Communication Method"
                name="method"
                required
                value={formData.method || []}
                error={!!errors.method}
                helperText={errors.method}
                onChange={(event) => {
                  const {
                    target: { value },
                  } = event;
                  handleChange({
                    target: {
                      name: "method",
                      value: typeof value === "string" ? value.split(";") : value,
                    },
                  });
                }}
                size="small"
                fullWidth
                SelectProps={{
                  multiple: true,
                  renderValue: (selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip
                          key={value}
                          label={value}
                          size="small"
                          onDelete={(e) => {
                            e.stopPropagation();
                            const newValue = formData.method.filter(
                              (item) => item !== value
                            );
                            handleChange({
                              target: {
                                name: "method",
                                value: newValue,
                              },
                            });
                          }}
                          onMouseDown={(e) => e.stopPropagation()}
                          deleteIcon={<CancelIcon />}
                        />
                      ))}
                    </Box>
                  ),
                }}
              >
                {communicationMethods.map((option) => (
                  <MenuItem key={option} value={option}>
                    <StyledCheckbox
                      checked={formData.method?.indexOf(option) > -1}
                    />
                    <ListItemText primary={option} />
                  </MenuItem>
                ))}
              </StyledTextField>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <StyledCheckbox
                  name="consentProvided"
                  checked={formData.consentProvided}
                  onChange={handleChange}
                />
              }
              label={
                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontFamily={fontfamily}
                >
                  I agree to be contacted by the company.
                </Typography>
              }
            />
          </Grid>
          <Grid item xs={12} mt={isMobile ? 0 : -9}>
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="outlined"
                onClick={handleCancel}
                sx={{
                  mt: 2,
                  px: 4,
                  py: 1,
                  fontFamily: fontfamily,
                  color: primaryColor,
                  borderColor: primaryColor,
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={isSubmitting}
                sx={{
                  mt: 2,
                  ml: 1,
                  px: 4,
                  py: 1,
                  fontFamily: fontfamily,
                  backgroundColor: primaryColor,
                  "&:hover": { backgroundColor: buttonHoverColor },
                  "&:disabled": {
                    backgroundColor: "grey.300",
                    cursor: "not-allowed",
                  },
                }}
              >
                {isSubmitting ? (
                  <>
                    <CircularProgress
                      size={10}
                      color="inherit"
                      style={{ marginRight: 8 }}
                    />
                    Next
                  </>
                ) : (
                  "Next"
                )}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
      {/* Cancel Modal */}
      <Modal open={openCancelModal} onClose={handleCloseModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            minWidth: 300,
            textAlign: "center",
            fontFamily: fontfamily,
          }}
        >
          <Typography
            sx={{
              fontSize: "20px",
              fontWeight: "bold",
              mb: 2,
              fontFamily: fontfamily,
            }}
          >
            Sorry to see you go
          </Typography>
          <Typography
            variant="body1"
            sx={{
              opacity: 0.9,
              fontSize: "16px",
              marginBottom: "12px",
              fontFamily: fontfamily,
            }}
          >
            But we hope to see you again soon!
          </Typography>
          <Button
            variant="contained"
            onClick={handleCloseModal}
            component="a"
            href="https://crm.bigfivecrm.com/"
            rel="noopener noreferrer"
            target="_blank"
            sx={{
              mt: 2,
              px: 4,
              py: 1,
              fontFamily: fontfamily,
              backgroundColor: primaryColor,
              "&:hover": { backgroundColor: buttonHoverColor },
              "&:disabled": {
                backgroundColor: "grey.300",
                cursor: "not-allowed",
              },
            }}
          >
            Close
          </Button>
        </Box>
      </Modal>
      {/* OTP Verification Modal */}
      <Modal open={openOtpModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            minWidth: 300,
            textAlign: "center",
            fontFamily: fontfamily,
          }}
        >
          <Typography
            variant="body1"
            sx={{
              opacity: 0.9,
              fontSize: "16px",
              marginBottom: "24px",
              fontFamily: fontfamily,
            }}
          >
            Enter the 6-digit OTP sent to{" "}
            {formData.emailAddress || formData.contactNumber}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              opacity: 0.9,
              fontSize: "14px",
              marginBottom: "24px",
              fontFamily: fontfamily,
              color: primaryColor,
            }}
          >
            We are processing your request...Please wait for few mins
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "center", gap: 1, mb: 2 }}>
            {otp.map((digit, index) => (
              <StyledOtpInput
                key={index}
                value={digit}
                onChange={(e) => handleOtpChange(index, e.target.value)}
                onKeyDown={(e) => handleOtpKeyDown(index, e)}
                onPaste={handleOtpPaste}
                inputRef={(el) => (otpInputs.current[index] = el)}
                inputProps={{ maxLength: 1 }}
                variant="outlined"
                error={!!otpError}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderColor: otpError ? "red" : undefined,
                  },
                }}
              />
            ))}
          </Box>
          {otpError && (
            <Typography color="error" sx={{ mb: 2, fontFamily: fontfamily }}>
              {otpError}
            </Typography>
          )}
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Button
              variant="outlined"
              onClick={handleCloseOtpModal}
              sx={{
                px: 4,
                py: 1,
                fontFamily: fontfamily,
                color: primaryColor,
                borderColor: primaryColor,
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleOtpSubmit}
              disabled={isVerify || otp.join("").length !== 6}
              sx={{
                px: 4,
                py: 1,
                fontFamily: fontfamily,
                backgroundColor: primaryColor,
                "&:hover": { backgroundColor: buttonHoverColor },
                "&:disabled": {
                  backgroundColor: "grey.300",
                  cursor: "not-allowed",
                },
              }}
            >
              {isVerify ? (
                <>
                  <CircularProgress
                    size={10}
                    color="inherit"
                    style={{ marginRight: 8 }}
                  />
                  Verify
                </>
              ) : (
                "Verify"
              )}
            </Button>
          </Box>
        </Box>
      </Modal>
      <UtilToastMessages
        isOpen={snackbar.open}
        handleClose={handleSnackbarClose}
        data={snackbar.message}
        severity={snackbar.severity}
      />
    </Box>
  );
};

export default UserDetails;